import DoDisturbOnOutlinedIcon from '@mui/icons-material/DoDisturbOnOutlined';
import { Box, Chip, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import isEqual from 'lodash.isequal';
import { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { FacilityType } from 'common/src/types/enums/FacilityType.enum';
import { ProductFeatures } from 'common/src/types/enums/ProductFeatures.enum';
import { Service } from 'common/src/types/enums/Service.enum';
import {
  ICreateFacilityDto,
  IUpdateFacilityDto,
} from 'common/src/types/facility.dtos';
import {
  IFacility,
  IFacilityGroup,
  IOrganization,
  IPage,
} from 'common/src/types/interfaces';

import {
  addFacilities,
  addFacilityToEnergyStar,
  updateEnergyStarScoreAndMetrics,
  updateFacilities,
} from 'portal/src/api/FacilitiesService';
import { getFacilityGroups } from 'portal/src/api/FacilityGroupsService';
import useAuth from 'portal/src/auth/useAuth';
import { ExtraButtonConfig } from 'portal/src/components/common/forms/FormButtonColumn';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import { prepareFormPayload } from 'portal/src/components/common/forms/handleFormData';
import useFormControlError from 'portal/src/components/common/forms/useFormControlError';
import { allOrgEsContactFieldsExist } from 'portal/src/components/common/ValidateOrganizationESContactInfo';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';

import {
  defaultValues,
  FacilityFormValues,
  getFields,
} from './FacilityForm.fields';

import AssociatedMeters from '../../Meters/AssociatedMeters';
import HistoricalValuesDialogTable from '../HistoricalValues/HistoricalValuesDialogTable';
import { useHistoricalValues } from '../HistoricalValues/useHistoricalValues';
import useSnackbarHandler from './../../../../utils/Snackbar/useSnackbarHandler';
import AddToEnergyStarDialog from './Dialogs/AddToEnergyStarDialog';
import ESScoreAndMetricsDialog from './Dialogs/ESScoreAndMetricsDialog';
enum ESValidations {
  invalidSquareFootage = 'Facility cannot be added to ENERGY STAR until a valid Square Footage is entered',
  invalidEnergyMeter = 'Facility cannot be added to ENERGY STAR without at least 1 energy meter',
  invalidESContactInfo = 'Facility cannot be added to ENERGY STAR until the ENERGY STAR contact has been added on the Organization admin screen',
}

interface FormComponentProps {
  entity?: Partial<IFacility>;
  handleBackAction: () => void;
}

function validateES(
  facility: Partial<IFacility>,
  organizationData: Partial<IOrganization>,
) {
  const errors: ESValidations[] = [];

  if (!facility.squareFootage || facility.squareFootage <= 0) {
    errors.push(ESValidations.invalidSquareFootage);
  }

  const validMeterTypes = [
    Service.Coal,
    Service.Diesel,
    Service.Electric,
    Service.FuelOil,
    Service.NaturalGas,
    Service.Propane,
    Service.Wood,
  ];

  const hasValidMeter = facility.meters?.some((meter) =>
    validMeterTypes.includes(meter.serviceType),
  );

  if (!hasValidMeter) {
    errors.push(ESValidations.invalidEnergyMeter);
  }

  const hasValidContactInfo =
    !!organizationData.esCustomerId ||
    allOrgEsContactFieldsExist(organizationData);

  if (!hasValidContactInfo) {
    errors.push(ESValidations.invalidESContactInfo);
  }

  return errors;
}

const FacilityForm = ({
  entity = {},
  handleBackAction,
}: FormComponentProps) => {
  const snackbarHandler = useSnackbarHandler();
  const { featureChecker, hasReadOnlyAccess, hasSystemAdminAccess, getUser } =
    useAuth();
  const { organization, organizationId } = getUser();
  const isDeactivated = entity.id && !entity.isActive;
  const [showConfirmEsDialog, setShowConfirmEsDialog] = useState(false);
  const [showConfirmEsScoreMetricsDialog, setShowConfirmEsScoreMetricsDialog] =
    useState(false);
  const [showAddToEnergyStar, setShowAddToEnergyStar] = useState(
    entity.id &&
      !entity.esPropertyId &&
      organization.esCustomerId &&
      featureChecker.productFeatureEnabled(
        ProductFeatures.energyStarManagement,
      ),
  );

  const { data: facilityGroups } = useQuery({
    queryKey: ['facilityGroups'],
    queryFn: async () => await getFacilityGroups(),
    initialData: { data: [] } as IPage<IFacilityGroup>,
  });

  const defaults = defaultValues(entity);

  const {
    handleSubmit,
    formState: { dirtyFields, isDirty },
    control,
    getValues,
    setError,
  } = useForm({ defaultValues: defaults });
  const [selectedCountry, selectedFacilityType] = useWatch({
    control,
    name: ['country', 'type'],
  });
  const { getSnackbarApiErrorMessage } = useFormControlError({
    getValues,
    setError,
  });

  const squareFootHistConfigs = useHistoricalValues(
    defaults.squareFootage,
    entity.squareFootageHistoricalValues,
    entity.createdAt,
  );
  const studentOcptsHistConfigs = useHistoricalValues(
    defaults.studentOccupants,
    entity.studentOccupantsHistoricalValues,
    entity.createdAt,
  );
  const totalOcptsHistConfigs = useHistoricalValues(
    defaults.totalOccupants,
    entity.totalOccupantsHistoricalValues,
    entity.createdAt,
  );

  const sqFootageChanged =
    squareFootHistConfigs.isDirty || dirtyFields.squareFootage;
  const studentOccupantsChanged =
    studentOcptsHistConfigs.isDirty || dirtyFields.studentOccupants;
  const totalOccupantsChanged =
    totalOcptsHistConfigs.isDirty || dirtyFields.totalOccupants;

  // TODO: un-comment this block so that the can control the values in the form when switching types
  // USE A CHANGE EVENT HANDLER INSTEAD OF AN EFFECT - https://react.dev/learn/you-might-not-need-an-effect
  // useEffect(() => {
  //   if (
  //     selectedFacilityType !== FacilityType.College &&
  //     selectedFacilityType !== FacilityType.Kto12
  //   ) {
  //     // set the value to an empty string, this forces the underlying NumberFormat component to clear the data
  //     setValue('studentOccupants', '' as any);
  //   }

  //   if (selectedFacilityType !== FacilityType.Kto12) {
  //     setValue('highschool', null);
  //   }
  // }, [
  //   selectedFacilityType,
  //   watchedStudentOccupants,
  //   watchedHighSchool,
  //   setValue,
  // ]);

  const esValidation = useMemo(() => {
    if (Object.keys(entity).length) {
      const errors = validateES(entity, organization);
      return { value: !!errors.length, errors };
    }

    return {
      value: false,
      errors: [],
    };
  }, [entity, organization]);

  const formFieldsConfiguration = getFields(
    facilityGroups.data,
    selectedFacilityType,
    squareFootHistConfigs.handleValueChange,
    studentOcptsHistConfigs.handleValueChange,
    totalOcptsHistConfigs.handleValueChange,
    entity.id ? squareFootHistConfigs.handleOpen : undefined,
    entity.id ? studentOcptsHistConfigs.handleOpen : undefined,
    entity.id ? totalOcptsHistConfigs.handleOpen : undefined,
    selectedCountry,
    hasSystemAdminAccess(),
  );

  const { mutate: tryAddFacilityToEnergyStar, isPending: isAddToESPending } =
    useMutation({
      mutationFn: async (esConsumptionSyncStartDate: string) =>
        await addFacilityToEnergyStar(entity.id, esConsumptionSyncStartDate),
      onSuccess: () => {
        snackbarHandler.success(MESSAGES.FACILITY.ADD_TO_ES);
        setShowConfirmEsDialog(false);
        setShowAddToEnergyStar(false);
      },
      onError: () => {
        snackbarHandler.error(MESSAGES.GENERIC.ERROR);
      },
    });

  const {
    mutate: tryUpdateEnergyStarScoreAndMetrics,
    isPending: isUpdateScoreAndMetricsPending,
  } = useMutation({
    mutationFn: async (metricsStartMonth: string) =>
      await updateEnergyStarScoreAndMetrics(entity.id, {
        metricsStartMonth,
      }),
    onSuccess: () => {
      snackbarHandler.success(MESSAGES.FACILITY.UPDATE_ES);
      setShowConfirmEsScoreMetricsDialog(false);
    },
    onError: () => {
      snackbarHandler.error(MESSAGES.GENERIC.ERROR);
      setShowConfirmEsScoreMetricsDialog(false);
    },
  });

  const formatPayload = (payload: FacilityFormValues) => {
    const docFacilityGroups =
      entity.facilityGroups?.map((group) => ({ id: group.id })) || [];
    const payloadFacilityGroups =
      payload.facilityGroups?.map((id) => ({ id })) || [];

    const formattedFields = {
      ...prepareFormPayload(
        {
          ...payload,
          facilityGroups: payloadFacilityGroups,
        },
        {
          ...dirtyFields,
          facilityGroups: !isEqual(docFacilityGroups, payloadFacilityGroups),
          // If the type is college or Kto!2 include the studentOccupants value regardless of whether it's changed or not
          ...((payload.type === FacilityType.College ||
            payload.type === FacilityType.Kto12) && {
            studentOccupants: true,
          }),
          // If the type is Kto!2 include the highschool value regardless of whether it's changed or not
          ...(payload.type === FacilityType.Kto12 && {
            highschool: true,
          }),
        },
      ),
    };

    return {
      ...formattedFields,
      ...(sqFootageChanged
        ? {
            squareFootageHistoricalValues:
              squareFootHistConfigs.formatPayload(),
          }
        : {}),
      ...(totalOccupantsChanged
        ? {
            totalOccupantsHistoricalValues:
              totalOcptsHistConfigs.formatPayload(),
          }
        : {}),
      ...(studentOccupantsChanged
        ? {
            studentOccupantsHistoricalValues:
              studentOcptsHistConfigs.formatPayload(),
          }
        : {}),
    };
  };

  const { mutate: mutateFacility, isPending: facilityMutationPending } =
    useMutation({
      mutationFn: async (payload: FacilityFormValues) => {
        const formattedPayload = formatPayload(payload);

        if (entity.id) {
          await updateFacilities(
            entity.id,
            formattedPayload as unknown as IUpdateFacilityDto,
          );
          snackbarHandler.success(MESSAGES.FACILITY.UPDATED);
        } else {
          await addFacilities({
            ...formattedPayload,
            organizationId,
          } as unknown as ICreateFacilityDto);
          snackbarHandler.success(MESSAGES.FACILITY.CREATED);
        }

        handleBackAction();
      },
      onError: (err: unknown) => {
        snackbarHandler.error(getSnackbarApiErrorMessage(err));
      },
    });

  const isDisabled =
    isDeactivated ||
    facilityMutationPending ||
    isAddToESPending ||
    isUpdateScoreAndMetricsPending ||
    hasReadOnlyAccess();

  const esExtraButtons: ExtraButtonConfig[] = [];

  if (showAddToEnergyStar) {
    esExtraButtons.push({
      isDisabled:
        entity.isDefault ||
        esValidation.value ||
        isAddToESPending ||
        facilityMutationPending ||
        hasReadOnlyAccess(),
      label: 'Add to ENERGY STAR',
      onClick: () => setShowConfirmEsDialog(true),
      tooltipMessage: entity.isDefault
        ? 'The default facility for new meters cannot be added to ENERGY STAR'
        : esValidation.errors.join(', '),
    });
  }

  if (entity.id && entity.esPropertyId) {
    esExtraButtons.push({
      label: 'View in ENERGY STAR',
      onClick: () =>
        window.open(
          `https://portfoliomanager.energystar.gov/pm/property/${entity.esPropertyId}`,
          '_blank',
          'noopener,noreferrer',
        ),
    });
    esExtraButtons.push({
      isDisabled:
        entity.isDefault ||
        facilityMutationPending ||
        isUpdateScoreAndMetricsPending ||
        hasReadOnlyAccess(),
      label: 'Update ENERGY STAR Scores and Metrics',
      onClick: () => setShowConfirmEsScoreMetricsDialog(true),
      tooltipMessage: entity.isDefault
        ? 'The default facility for new meters cannot have its ENERGY STAR scores and metrics updated'
        : undefined,
    });
  }

  return (
    <FormContainer
      control={control}
      extraButtons={esExtraButtons.length > 0 ? esExtraButtons : undefined}
      handleBackAction={handleBackAction}
      handleSubmit={handleSubmit((data) => mutateFacility(data))}
      header={
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">Facility Information</Typography>
            {entity.isDefault && (
              <Chip
                color="warning"
                label="Default For New Meters"
                size="small"
              />
            )}
          </Box>
          {isDeactivated && (
            <Chip
              icon={
                <DoDisturbOnOutlinedIcon
                  fontSize="small"
                  aria-label="Deactivated"
                />
              }
              color="error"
              label="DEACTIVATED"
              size="small"
            />
          )}
        </Box>
      }
      isDeactivated={!!isDeactivated}
      isDirty={
        isDirty ||
        sqFootageChanged ||
        studentOccupantsChanged ||
        totalOccupantsChanged
      }
      isNew={!entity.id}
      isPending={facilityMutationPending}
      mainFieldsConfig={formFieldsConfiguration}
      afterMainFields={
        <>
          {entity?.id && (
            <>
              <AssociatedMeters
                form="facility"
                docId={entity.id}
                isDisabled={!!isDisabled}
              />
              <HistoricalValuesDialogTable
                historyConfigs={squareFootHistConfigs}
                targetHeaderName="Square Footage"
                targetColumnType="number"
              />
              <HistoricalValuesDialogTable
                historyConfigs={studentOcptsHistConfigs}
                targetHeaderName="Student Occupants"
                targetColumnType="number"
              />
              <HistoricalValuesDialogTable
                historyConfigs={totalOcptsHistConfigs}
                targetHeaderName="Total Occupants"
                targetColumnType="number"
              />
            </>
          )}
          <AddToEnergyStarDialog
            open={showConfirmEsDialog}
            onClose={() => setShowConfirmEsDialog(false)}
            onSave={(syncStartDate) =>
              tryAddFacilityToEnergyStar(syncStartDate)
            }
            isLoading={isAddToESPending}
            facilityName={entity.name}
            products={organization.products}
          />
          <ESScoreAndMetricsDialog
            open={showConfirmEsScoreMetricsDialog}
            onClose={() => setShowConfirmEsScoreMetricsDialog(false)}
            onSave={(data) => tryUpdateEnergyStarScoreAndMetrics(data)}
          />
        </>
      }
    />
  );
};

export default FacilityForm;
